import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { closeWorkOrderRepair, fetchWorkOrderRepairList } from '#/api/momMgmt/workOrderRepair';
import { PERM } from '#/config/permissions';
import useDetailTable from '#/hooks/table/useDetailTable';
import { StatusBadge } from '#/modules/commonComponents/badge';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import DetailTable from '#/modules/commonComponents/table/DetailTable';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { useCan } from '#/modules/infra/auth/useCan';
import WorkOrderRepairCorrectionDialog from '#/modules/momMgmt/workOrder/components/WorkOrderRepairCorrectionDialog';
import WorkOrderRepairDialog from '#/modules/momMgmt/workOrder/components/WorkOrderRepairDialog';
import {
  WORK_ORDER_REPAIR_STATUS,
  canWorkOrderHaveRepairs,
  formatWorkOrderRepairStatus,
  getWorkOrderRepairErrorMessage,
  isWorkOrderRepairQtyBalanced,
} from '#/modules/momMgmt/workOrder/helper/workOrderRepair';
import { formatDateTimeOrDash } from '#/utils/date';
import { formatDisplayQty } from '#/utils/formatNumber';
import { toast } from '#/utils/toast';

const PAGE_LIMIT = 50;

function workOrderRepairListQueryKey(workOrderId) {
  return ['erp', 'workOrderRepair', 'list', workOrderId];
}

function buildColumns({ onView, onClose, onCorrect, canUpdate, canClose, canCorrect, closingId }) {
  return [
    {
      id: 'repairNo',
      header: '維修單號',
      accessorFn: (row) => row.repairNo || '—',
      size: 160,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => (
        <button
          type="button"
          className="font-mono text-primary-700 hover:underline"
          onClick={() => onView(row.original)}
        >
          {row.original.repairNo || '—'}
        </button>
      ),
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      // DetailTable 對 id=status 預設畫全站狀態 icon；這裡用自訂中文標籤
      meta: { statusPresentation: 'badge' },
      size: 90,
      enableSorting: false,
      cell: ({ row }) => (
        <StatusBadge
          status={row.original.status}
          label={formatWorkOrderRepairStatus(row.original.status)}
          size="sm"
        />
      ),
    },
    {
      id: 'problemQty',
      header: '問題數',
      accessorFn: (row) => formatDisplayQty(row.problemQty),
      size: 80,
      enableSorting: false,
      meta: { cellClassName: 'td_right' },
    },
    {
      id: 'repairedQty',
      header: '已修復',
      accessorFn: (row) => formatDisplayQty(row.repairedQty),
      size: 80,
      enableSorting: false,
      meta: { cellClassName: 'td_right' },
    },
    {
      id: 'notRepairedQty',
      header: '不維修',
      accessorFn: (row) => formatDisplayQty(row.notRepairedQty),
      size: 80,
      enableSorting: false,
      meta: { cellClassName: 'td_right' },
    },
    {
      id: 'problemDescription',
      header: '問題描述',
      accessorFn: (row) => row.problemDescription || '—',
      size: 220,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ getValue }) => <TruncatedTextWithTooltip text={getValue()} />,
    },
    {
      id: 'materialCount',
      header: '用料',
      accessorFn: (row) => row.materials?.length ?? 0,
      size: 60,
      enableSorting: false,
    },
    {
      id: 'closedAt',
      header: '結單時間',
      accessorFn: (row) => formatDateTimeOrDash(row.closedAt),
      size: 150,
      enableSorting: false,
    },
    {
      id: 'actions',
      header: '',
      size: 170,
      enableSorting: false,
      cell: ({ row }) => {
        const repair = row.original;
        const isOpen = repair.status === WORK_ORDER_REPAIR_STATUS.OPEN;
        return (
          <div className="flex justify-end gap-1">
            {isOpen && canUpdate ? (
              <Button type="button" size="sm" variant="outline" onClick={() => onView(repair)}>
                編輯
              </Button>
            ) : null}
            {isOpen && canClose ? (
              <Button
                type="button"
                size="sm"
                disabled={closingId === repair.id}
                onClick={() => onClose(repair)}
              >
                結單
              </Button>
            ) : null}
            {!isOpen && canCorrect ? (
              <Button type="button" size="sm" variant="outline" onClick={() => onCorrect(repair)}>
                更正
              </Button>
            ) : null}
          </div>
        );
      },
    },
  ];
}

/** 工單 detail「維修單」分頁：列表（cursor 載入更多）＋新增／編輯／結單／更正。 */
const WorkOrderRepairsPanel = ({ workOrderId, workOrderStatus, onChanged }) => {
  const queryClient = useQueryClient();
  const { confirm, confirmDialog } = useConfirmDialog();
  const canRead = useCan(PERM.WORK_ORDER_REPAIR_READ);
  const canCreate = useCan(PERM.WORK_ORDER_REPAIR_CREATE);
  const canUpdate = useCan(PERM.WORK_ORDER_REPAIR_UPDATE);
  const canClose = useCan(PERM.WORK_ORDER_REPAIR_CLOSE);
  const canCorrect = useCan(PERM.WORK_ORDER_REPAIR_CORRECT);
  const parentAllowsRepair = canWorkOrderHaveRepairs(workOrderStatus);

  const [dialogRepair, setDialogRepair] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [correctionTarget, setCorrectionTarget] = useState(null);
  const [correctionSeq, setCorrectionSeq] = useState(0);

  const listQuery = useInfiniteQuery({
    queryKey: workOrderRepairListQueryKey(workOrderId),
    queryFn: ({ pageParam }) => fetchWorkOrderRepairList({
      workOrderId,
      limit: PAGE_LIMIT,
      cursor: pageParam,
    }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
    enabled: Boolean(workOrderId) && canRead && parentAllowsRepair,
  });
  const rows = useMemo(
    () => (listQuery.data?.pages ?? []).flatMap((page) => page?.data ?? []),
    [listQuery.data?.pages],
  );

  function refresh() {
    queryClient.invalidateQueries({ queryKey: workOrderRepairListQueryKey(workOrderId) });
    onChanged?.();
  }

  const closeMutation = useMutation({
    mutationFn: (repair) => closeWorkOrderRepair(repair.id, {
      updatedAt: repair.record?.updatedAt,
      revision: repair.revision,
    }),
    onSuccess: () => toast.success('已結單，用料已扣帳'),
    onError: (error) => toast.error(getWorkOrderRepairErrorMessage(error, '結單失敗')),
    onSettled: refresh,
  });

  async function handleClose(repair) {
    if (!isWorkOrderRepairQtyBalanced(repair)) {
      toast.error('問題數量須等於已修復數量＋不維修數量才能結單');
      return;
    }
    const materialCount = repair.materials?.length ?? 0;
    const ok = await confirm({
      title: '維修單結單',
      message: materialCount > 0
        ? `結單後會從所選庫存扣帳 ${materialCount} 筆用料，且不能再修改（錯誤只能用更正沖回）。確定結單？`
        : '結單後不能再修改。確定結單？',
      confirmText: '結單',
    });
    if (ok) {
      closeMutation.mutate(repair);
    }
  }

  function openDialog(repair) {
    setDialogRepair(repair);
    setDialogOpen(true);
  }

  function openCorrection(repair) {
    setCorrectionSeq((seq) => seq + 1);
    setCorrectionTarget(repair);
  }

  const { isPending: isClosing, variables: closingRepair } = closeMutation;
  const closingId = isClosing ? closingRepair?.id : null;
  const columns = useMemo(
    () => buildColumns({
      onView: openDialog,
      onClose: handleClose,
      onCorrect: openCorrection,
      canUpdate,
      canClose,
      canCorrect,
      closingId,
    }),
    // handlers 只用 setState／confirm／mutate
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canUpdate, canClose, canCorrect, closingId],
  );
  const detailTableState = useDetailTable(rows, columns, {
    enableExpanding: false,
    getRowId: (row) => String(row.id),
  });

  if (!parentAllowsRepair) {
    return <p className="px-4 py-5 text-sm text-muted-foreground">工單核准後（生產中、已結案或已中止）才能開維修單。</p>;
  }
  if (!canRead) {
    return <p className="px-4 py-5 text-sm text-muted-foreground">沒有讀取工單維修單的權限。</p>;
  }

  return (
    <div className="flex flex-col gap-3 py-3">
      {canCreate ? (
        <div className="flex justify-end px-4">
          <Button type="button" size="sm" onClick={() => openDialog(null)}>新增維修單</Button>
        </div>
      ) : null}
      {listQuery.isError ? (
        <p className="px-4 text-sm text-destructive">
          {getWorkOrderRepairErrorMessage(listQuery.error, '載入維修單失敗')}
        </p>
      ) : null}
      {rows.length === 0 && !listQuery.isLoading ? (
        <p className="px-4 py-2 text-sm text-muted-foreground">尚無維修單</p>
      ) : null}
      {rows.length > 0 ? (
        <div className="detail-table-container">
          <DetailTable
            state={detailTableState}
            containerClassName="detail-table-scroll--section-embedded"
            tableClassName="detail-table--flat-lines"
          />
        </div>
      ) : null}
      {listQuery.hasNextPage ? (
        <div className="flex justify-center">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={listQuery.isFetchingNextPage}
            onClick={() => listQuery.fetchNextPage()}
          >
            {listQuery.isFetchingNextPage ? '載入中…' : '載入更多'}
          </Button>
        </div>
      ) : null}

      <WorkOrderRepairDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        workOrderId={workOrderId}
        repair={dialogRepair}
        canEdit={canUpdate}
        onSaved={refresh}
      />
      {correctionTarget ? (
        <WorkOrderRepairCorrectionDialog
          key={`${correctionTarget.id}-${correctionSeq}`}
          open
          onOpenChange={(next) => {
            if (!next) {
              setCorrectionTarget(null);
            }
          }}
          repair={correctionTarget}
          onPosted={() => {
            queryClient.invalidateQueries({ queryKey: ['erp', 'workOrderRepair', 'corrections', correctionTarget.id] });
            refresh();
          }}
        />
      ) : null}
      {confirmDialog}
    </div>
  );
};

WorkOrderRepairsPanel.propTypes = {
  workOrderId: PropTypes.string,
  workOrderStatus: PropTypes.string,
  onChanged: PropTypes.func,
};

export default WorkOrderRepairsPanel;
